import * as React from 'react';
import { Address4, Address6 } from 'ip-address';
import FormModel from '../../../../utils/FormModel';
import PortOpeningRuleOnPrem from '../../../../models/entities/PortOpeningRuleOnPrem';
import { RuleAction } from '../../../../models/enums/POR/RuleAction';
import { AbstractFormState, AbstractForm } from '../../../common/AbstractForm';
import { ValidationRule } from '../../../../utils/Validation';
import { RuleOperation } from '../../../../models/enums/POR/RuleOperation';
import { PortOpeningRequestsDirection } from '../../../../models/enums/POR/PortOpeningRequestsDirection';
import { start } from 'repl';

export interface RulesFormProps {
	visible: boolean;
	onConfirm: (rule: PortOpeningRuleOnPrem) => void;
	onCancel?: (event: React.ChangeEvent<any>) => void;
	onClose: (event: React.ChangeEvent<any>) => void;
	editingRule?: PortOpeningRuleOnPrem;
	isReadOnly?: boolean;
	isFromHistory?: boolean;
	direction?: PortOpeningRequestsDirection;
 }

export interface IRuleStateForm {
	name: string;
	operation: RuleOperation;
	action: RuleAction;
	isTemporary?: boolean;
	sourceIPs: string;
	sourceObjects: string;
	destinationIPs: string;
	destinationPorts: string;
	destinationObjects: string;
	description: string;
}

/** @abstract */
export abstract class AbstractRulesForm<
	Props extends RulesFormProps,
	State extends AbstractFormState<IRuleStateForm>
> extends AbstractForm<Props, State> {
	
	componentDidUpdate(prevProps) {
		if (this.props.editingRule !== prevProps.editingRule) {
			if (this.props.editingRule != null)
				this.setState({ stateForm: this.getRuleCopy() });
			else
				this.setState({ stateForm: this.getDefaultForm() });
		}
		if(prevProps.direction !== this.props.direction){
			if(this.props.direction?.name == PortOpeningRequestsDirection.iBossProxyToInternet.name)
				this.handleStateFormChange('sourceObjects', 'iBoss Proxy');
		}
	}

	parseMultipleValues = (values: string) => {
		return values?.split(/\n/).join(';')
			.split(' ').join('');
	};

	hasEmptyValues = (values: string): boolean => {
		return this.parseMultipleValues(values)?.split(';').some(value => {			
			return value.length === 0;
		});
	};

	hasWhiteSpaces = (values: string): boolean => {
		return values.split(';').some(value => {
	        return value.includes(' ');
	    });
	};

	isPrivateAddress = (ipAddress: Address4): boolean => {
		const privateSubnets = [
			'10.0.0.0/8'       // RFC1918 Class A
		];

		return privateSubnets.some(subnet => ipAddress.isInSubnet(new Address4(subnet)));
	};

	isIpAddressInputValid = (input: string, onlyPrivate: boolean): boolean => {
		try {
			if (input.includes('-')) {
				const parts = input.split('-');
				if (parts.length !== 2) {
					return false;
				}
				const [startRange, endRange] = parts;
				const startAddress = new Address4(startRange);
				const endAddress = new Address4(endRange);

				return (startAddress.toHex() < endAddress.toHex()) &&
					(!onlyPrivate ||
						(this.isPrivateAddress(startAddress) && this.isPrivateAddress(endAddress)));
			}
			const ip = new Address4(input);
			return ip.isCorrect()			
		} catch {
			return false;
		}
	};

	// TODO: Move all these IPs validations, methods, etc.. to a separate/dedicated service
	isIpInAnyAddressSpace = (ipAddress: string, validAddressSpaces: string[]): boolean => {
		return !!validAddressSpaces?.length && validAddressSpaces.some(addressSpace => this.isIpInAddressSpace(ipAddress, addressSpace));
	}

	isIpInAddressSpace = (ipAddress: string, addressSpace: string): boolean => {
		try {
			var ip6 = new Address6(ipAddress);
			var addressSpace6 = new Address6(addressSpace);

			if (addressSpace6.isCorrect()) {
				return ip6.isInSubnet(addressSpace6);
			} else {
				var ip4 = new Address4(ipAddress);
				var addressSpace4 = new Address4(addressSpace);
				return ip4.isInSubnet(addressSpace4);
			}
		} catch (ex) {
			try {
				var ip4 = new Address4(ipAddress);
				var addressSpace4 = new Address4(addressSpace);
				return ip4.isInSubnet(addressSpace4);
			}
			catch (ex) {
				return false
			}
		}
	}

	isPortInputValid = (value: string, minimumPort: number): boolean => {
		const ValidPort = (port) => {
			if(port.includes(",")) return -1;
			const maxmimumPort = 65535;
			const parsedPort = parseInt(port);

			if (!isNaN(parsedPort) && parsedPort >= minimumPort && parsedPort <= maxmimumPort)
				return parsedPort;
			return -1;
		};
		
		if (/^([0-9])*([-])([0-9])*$/.test(value)) {
			const [startRange, endRange] = value.split('-').map(p => ValidPort(p));
			return (startRange > -1 && endRange > -1 && startRange < endRange);
		}
		return ValidPort(value) > -1;
	};

	isModalForNewRule() {
		return !this.props.isReadOnly && this.stateFormHandler().operation.value === RuleOperation.Add.value;
	}

	getStateFormHandlers() {
		return {
			name: {
				value: this.state.stateForm.name,
				validation: this.formModel?.fields.name.validation,
				onChange: (event) => {
					this.handleStateFormChange('name', event.target.value);
				},
			},
			operation: {
				value: this.state.stateForm.operation?.value,
				validation: this.formModel?.fields.operation.validation,
				onChange: (event) => {
					this.handleStateFormChange('operation', new RuleOperation().fromValue(event.target.value));
				},
				options: (this.state.stateForm.operation?.value === RuleOperation.Add.value) ?
				new RuleOperation().list().sort((a, b) => a.name > b.name ? 1 : -1) : [
					RuleOperation.Remove
				],
				disabled: this.state.stateForm.operation
			},
			action: {
				label: 'Action',				
				value: this.state.stateForm.action?.value,
				validation: this.formModel?.fields.action.validation,
				disabled: this.state.stateForm.action,
				onChange: (event) => {
					this.handleStateFormChange('action', new RuleAction().fromValue(event.target.value));
				}
			},
			isTemporary: {
				label: 'Type',				
				value: this.state.stateForm.isTemporary,
				validation: this.formModel?.fields.isTemporary?.validation,
				disabled: this.state.stateForm.isTemporary,
				onChange: (event) => {
					this.handleStateFormChange('isTemporary', event.target.value);
				},
				note: this.state.stateForm.isTemporary?.toString()=='true' ? 'Temporary rule will be deleted in 30 days' : ''
			},
			sourceIPs: {
				label: 'Source IPs',
				value: this.state.stateForm.sourceIPs,
				validation: this.formModel?.fields.sourceIPs.validation,
				onChange: (event) => {
					this.handleStateFormChange('sourceIPs', event.target.value);
				},
				note: 'Multiple values are allowed, separated by semicolons.'
			},
			sourceObjects: {
				value: this.state.stateForm.sourceObjects,
				validation: this.formModel?.fields.sourceObjects.validation,
				onChange: (event) => {
					this.handleStateFormChange('sourceObjects', event.target.value);
				},
				note: 'Multiple values are allowed, separated by semicolons.'
			},
			destinationIPs: {
				label: 'Destination IP',
				value: this.state.stateForm.destinationIPs,
				validation: this.formModel?.fields.destinationIPs.validation,
				onChange: (event) => {
					this.handleStateFormChange('destinationIPs', event.target.value);
				},
				note: 'Multiple values are allowed, separated by semicolons.'
			},
			destinationPorts: {
				label: 'Destination Port(s)',
				value: this.state.stateForm.destinationPorts,
				validation: this.formModel?.fields.destinationPorts.validation,
				onChange: (event) => {
					this.handleStateFormChange('destinationPorts', event.target.value);
				},
				note: 'Multiple values are allowed, separated by semicolons. Port ranges are accepted.'
			},
			destinationObjects: {
				value: this.state.stateForm.destinationObjects,
				validation: this.formModel?.fields.destinationObjects.validation,
				onChange: (event) => {
					this.handleStateFormChange('destinationObjects', event.target.value);
				},
				note: 'Multiple values are allowed, separated by semicolons.'
			},
			description: {
				label: 'Description',
				value: this.state.stateForm.description,
				validation: this.formModel?.fields.description.validation,
				onChange: (event) => {
					if (event.target.value.length <= 140) {
						this.handleStateFormChange('description', event.target.value);
					}
				},
				note: `${this.state.stateForm.description.length} / 140` 
			}
		};
	}
	
	getDefaultForm() {
		const defaultForm = {
			isTemporary: null,
			name: '',
			operation: null,
			action: null,
			sourceObjects: '',
			sourceIPs: '',
			sourceEnvironment: '',
			destinationIPs: '',
			destinationPorts: '',
			destinationObjects: '',
			destinationEnvironment: '',
			description: '',
			...this.getDefaultFormState()
		};
	
		if (this.props.direction?.name === PortOpeningRequestsDirection.iBossProxyToInternet.name) {
			defaultForm.sourceObjects = 'iBoss Proxy';
		}
	
		return defaultForm;
	}

	abstract getDefaultFormState();

	getRuleCopy() {
		return { ...this.props.editingRule };
	}

	handleCancel = (e) => {
		this.resetState();
		this.props.onClose(e);
	};

	handleFormChange = (event, field: string) => {
		const stateForm = { ...this.state.stateForm };
		stateForm[field] = event.target.value;
		this.setState({ stateForm });
	};

	handleRequest = async (e) => {
		this.props.onClose(e);
		const model = this.formModel.create(PortOpeningRuleOnPrem);
		this.props.onConfirm(model);
		this.resetState();
	};

	formCanBeEdited() {
		return !this.props.isReadOnly;
	}

	handleStateFormChange(field: string, value: any): void;
	handleStateFormChange(field: string[], value: any[]): void;

	handleStateFormChange(field, value): void {
		const fields = [].concat(...[field]);
		const values = !Array.isArray(field) ? [value] : value;
		const form = this.state.stateForm;
		fields.forEach((fieldName, index) => {
			form[fieldName] = values[index];
		});

		this.setState({ stateForm: form });
	}

	initFormModel() {
		this.formModel = new FormModel(
			{
				name: {
					getValue: () => this.state.stateForm.name,
					validation: {
						required: true,
						rules:
							this.state.stateForm.operation?.name === RuleOperation.Add.name &&
							[
								{
									assert: () => {
										return (this.state.stateForm.name?.length <= 15) || (this.state.stateForm.operation?.name !== RuleOperation.Add.name);
									},
									message: 'Name cannot be longer than 15 characters.',								  
								},
								{
									assert: () => !this.state.stateForm.name?.includes(' '),
									message: 'Name cannot contain spaces.',
								},
								{
									assert: () => /^[a-zA-Z](?:[a-zA-Z0-9_.-]*[a-zA-Z0-9])?$/.test(this.state.stateForm.name ?? ''),
									message: 'Name must begin with a letter, end with a letter or number, and may contain only letters, numbers, underscores, periods or dashes.',
								}
							]
					}
				},
				operation: {
					getValue: () => this.state.stateForm.operation,
					validation: {
						required: true,
					},
				},
				action: {
					getValue: () => this.state.stateForm.action,
					validation: {
						required: true,
					},
				},
				isTemporary: {
					getValue: () => this.state.stateForm.isTemporary,
					validation: {
						required: true,						
					},
				},
				sourceIPs: {
					getValue: () => this.state.stateForm.sourceIPs,
					validation: {
						required: () => true,
						rules: [
							{
								assert: () => !this.hasEmptyValues(this.state.stateForm.sourceIPs),
								message: 'Empty values are not allowed. Multiple values are allowed, separated by semicolons.'
							},
							{
								assert: () => !this.hasWhiteSpaces(this.state.stateForm.sourceIPs),
								message: 'Cannot contain white spaces.'
							}
						]
					}
				},
				sourceObjects: {
					getValue: () => this.state.stateForm.sourceObjects,
					validation: {
						required: () => false,
					}
				},
				destinationIPs: {
					getValue: () => this.parseMultipleValues(this.state.stateForm.destinationIPs),
					validation: {
						required: true,
						rules: [
							{
								assert: () => !this.hasEmptyValues(this.state.stateForm.destinationIPs),
								message: 'Empty values are not allowed. Multiple values are allowed, separated by semicolons.'
							},
							{
								assert: () => !this.hasWhiteSpaces(this.state.stateForm.destinationIPs),
								message: 'Cannot contain white spaces.'
							}
						]
					},
				},
				destinationPorts: {
					getValue: () => this.parseMultipleValues(this.state.stateForm.destinationPorts),
					validation: {
						required: true,
						rules: [
							{
								assert: () => !this.hasEmptyValues(this.state.stateForm.destinationPorts),
								message: 'Empty values are not allowed.'
							}
						]
					},
				},
				destinationObjects: {
					getValue: () => this.state.stateForm.destinationObjects,
					validation: {
						required: () => false,
					}
				},
				description: {
					getValue: () => this.state.stateForm.description,
					validation: {
						required: true,
						rules: [
							{
								assert: () => this.state.stateForm.description?.trim().length >= 5,
								message: 'Description must have at least 5 characters.',
							}
						],
					},
				}
			}
		);
	}

	PortInputValidationRule = ({ minimumPort }): ValidationRule => ({
		assert: (value) => {
			const ports = this.parseMultipleValues(value).split(';');
			return ports.every(port => this.isPortInputValid(port, minimumPort));
		},
		message: (value) => {
			const invalidPort = this.parseMultipleValues(value).split(';').find(port => !this.isPortInputValid(port, minimumPort));
			return `${invalidPort} is an invalid port input.`;
		}
	});

	IpInputValidationRule = ({ onlyPrivateAddresses }): ValidationRule => ({
		assert: (value) => {
			const addresses = this.parseMultipleValues(value).split(';');
			return addresses.every(address => this.isIpAddressInputValid(address, onlyPrivateAddresses));
		},
		message: (value) => {
			const invalidIp = this.parseMultipleValues(value).split(';').find(address => !this.isIpAddressInputValid(address, onlyPrivateAddresses));
			return `${invalidIp} is an invalid address.`;
		}
	});

	getlargeSubnetAlerttext(){
		return 'Provide a clear business justification for the use of subnets larger than /26';
	}

	assertSourceIPSContainsLargeSubnet(){
		var sourceIpsField = this.formModel.fields['sourceIPs']
		return sourceIpsField.validation.assert(sourceIpsField.getValue()) && this.ipsContainsLargeSubnet(this.state.stateForm.sourceIPs);
	}

	assertDestinationIPSContainsLargeSubnet(){
		var destinationIpsField = this.formModel.fields['destinationIPs']
		return destinationIpsField.validation.assert(destinationIpsField.getValue()) &&  this.ipsContainsLargeSubnet(this.state.stateForm.destinationIPs);
	}

	getAmountOfHosts(startIp:string, endIp:string){
		var startIP = new Address4(startIp).bigInteger();
		var endIP = new Address4(endIp).bigInteger();

		return (endIP - startIP) + 1;
	}

	isLargeSubnet(ip:string){
		if (ip.includes("/")) return new Address4(ip).subnetMask <= 26;
		else if(ip.includes("-")){
			var rangeIp = ip.split("-");
			var hosts = this.getAmountOfHosts(rangeIp[0],rangeIp[1])
			return hosts >= 62;
		} else return false;
	}

	ipsContainsLargeSubnet(ips: string){
		var IPs = ips.replace(/\n/g, ';').split(";")
		return IPs.some(ip => this.isLargeSubnet(ip))   
	}

	/*IpIsInAddressSpacesRule = (): ValidationRule => ({
		assert: (sourceIpsInput) => {
			const sourceIps = this.parseMultipleValues(sourceIpsInput).split(';');
			const subnets = this.props.validVirtualNetworks.flatMap(vNet => vNet.properties.subnets);
			const addressSpaces = subnets.flatMap(subnet => subnet.properties.addressPrefix);
			return sourceIps.every(ip => this.isIpInAnyAddressSpace(ip, addressSpaces));
		},
		message: (sourceIpsInput) => {
			const subnets = this.props.validVirtualNetworks.flatMap(vNet => vNet.properties.subnets);
			const addressSpaces = subnets.flatMap(subnet => subnet.properties.addressPrefix);
			const invalidIp = this.parseMultipleValues(sourceIpsInput).split(';').find(ip => !this.isIpInAnyAddressSpace(ip, addressSpaces));
			return `${invalidIp} is not part of any valid address space.`;
		}
	});*/

	resetState = () => {
    	this.setState({ stateForm: this.getDefaultForm() });
	};
	
	stateFormHandler = () => this.getStateFormHandlers();
}